import React, { useState, useEffect } from "react";
import clsx from "clsx";
import {
  Box,
  Card,
  Grid,
  makeStyles,
  FormControl,
  TextField,
  Button,
  Paper,
  FormControlLabel,
  Checkbox,
  FormLabel
} from "@material-ui/core";
import { useForm, Controller } from "react-hook-form";
import { kmMessages } from "src/constant/Messages";
import { DropzoneAreaBase } from "material-ui-dropzone";
import TableContainer from "@material-ui/core/TableContainer";
import { useTheme } from "@material-ui/core/styles";
import KmFormEditTable from "src/components/common/KmFormEditTable";
import InsertDriveFileIcon from "@material-ui/icons/InsertDriveFile";
import SendIcon from "@material-ui/icons/Send";
import {
  fillMissingTags
} from "src/utils/utils";

const subjectMaxLength = 100;
const bodyMaxLength = 1000;

const formValidation = {
  required: kmMessages.error.form_validation.field_required,
  maxLength: `${kmMessages.error.form_validation.field_maxlengthnum}`,
};

const useStyles = makeStyles((theme) => ({
  root: {
    "& > *": {
      margin: theme.spacing(2),
    },
  },
  formcontrol: {
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  paddingbottom: {
    paddingBottom: theme.spacing(1),
  },
  paddingtop: {
    paddingTop: theme.spacing(2),
  },
  button: {
    margin: theme.spacing(1),
  },
  rtepaper: {
    paddingBottom: theme.spacing(4),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
  rtecontroll: {
    "& > ol": {
      paddingLeft: theme.spacing(4),
    },
  },
  attachmentdialog: {
    minWidth: 500,
  },
  img: {
    margin: "auto",
    display: "block",
    maxHeight: 60,
    maxWidth: "auto",
  },
  addresscard: {
    padding: theme.spacing(1),
  },
}));

const attachmentCols = [
  {
    label: "L.p.",
    isLp: true,
    isEditable: false,
    width: "5%",
  },
  {
    label: "Typ",
    fieldName: "type",
    isEditable: false,
    width: "5%",
  },
  {
    label: "Nazwa",
    fieldName: "name",
    isEditable: false,
    width: "40%",
  },
  {
    label: "Plik/Odnośnik",
    fieldName: "details",
    isEditable: false,
    width: "40%",
    isHtml: true,
  },
];

const Form = ({ displaySnackbar, onSubmit, sendingSuccess, ...rest }) => {
  const classes = useStyles();

  const [attachments, setAttachments] = useState([]);
  const [copySend, setCopySend] = useState(false);

  const theme = useTheme();

  const { handleSubmit, setValue, control, errors } = useForm();

  useEffect(() => {
    if (sendingSuccess){
      setValue("subject","");
      setValue("body","");
      setAttachments([]);
      setCopySend(false);
      sendingSuccess = false;
    }
    setTimeout(fillMissingTags(),1000);
    return () => {
    }
  }, [sendingSuccess])

  const onFormSubmit = (values) => {
    onSubmit(values, copySend, attachments);
  };

  const handleAttachmentsAdd = (newFiles) => {
    newFiles = newFiles.filter(
      (file) => !attachments.find((f) => f.resName === file.file.name)
    );

    if (newFiles.length + attachments.length > 3){
      displaySnackbar({
        isOpen: true,
        severity: "error",
        autoHideDuration: 5000,
        message: "Maksymalna liczba załączników wynosi 3",
      });
    } else {
      let newAttachments = newFiles.map((file, index) => {
        return {
          id: `${new Date().getTime()}${index}`,
          type: <InsertDriveFileIcon />,
          attachmentType: "file",
          fileData: file,
          name: file.file.name,
          resName: file.file.name,
          details: file.file.name,
        };
      });
  
      setAttachments([...attachments, ...newAttachments]);
    }
  };


  const handleSendCopyChange = (event) => {
    setCopySend(event.target.checked);
  };

  return (
    <Card className={clsx(classes.root)}>
      <Box component="main">
        <Grid container className={classes.root}>
          <Grid item xs={12} elevation={2}>
            <form onSubmit={handleSubmit(onFormSubmit)}>
              <FormControl fullWidth className={classes.formcontrol}>
                <Controller
                  as={
                    <TextField
                      error={errors.subject ? true : false}
                      id="subject"
                      name="subject"
                      label="Temat wiadomości"
                      placeholder="Temat wiadomości"
                      helperText={
                        errors.subject &&
                        (formValidation[errors.subject.type] || "").replaceAll(
                          "{maxlen}",
                          subjectMaxLength
                        )
                      }
                    />
                  }
                  name="subject"
                  control={control}
                  rules={{ required: true, maxLength: subjectMaxLength }}
                />
              </FormControl>
              <FormControl fullWidth className={classes.formcontrol}>
                <Controller
                  as={
                    <TextField
                      error={errors.body ? true : false}
                      id="body"
                      name="body"
                      label="Treść wiadomości"
                      placeholder="Treść wiadomości"
                      multiline
                      helperText={
                        errors.body &&
                        (formValidation[errors.body.type] || "").replaceAll(
                          "{maxlen}",
                          bodyMaxLength
                        )
                      }
                    />
                  }
                  name="body"
                  control={control}
                  rules={{ required: true, maxLength: bodyMaxLength }}
                />
              </FormControl>
              <FormControl
                fullWidth
                className={classes.formcontrol}
                variant="outlined"
                label="Załączniki"
              >
                <Box className={classes.paddingbottom}>
                  <FormLabel htmlFor="attachmentsfileupload">Załączniki (Maksymalnie 3 pliki po 3MB):</FormLabel>
                </Box>
                <DropzoneAreaBase
                  showAlerts={false}
                  name="attachmentsfileupload"
                  className={classes.fileUpload}
                  previewText="Wybrane pliki:"
                  filesLimit={4}
                  maxFileSize={3145728}
                  dropzoneText="Przeciągnij i upuść pliki lub kliknij"
                  inputProps={{
                    title: "Dodaj pliki",
                    id: "attachmentsfileupload",
                  }}
                  showPreviews={false}
                  showPreviewsInDropzone={false}
                  showFileNamesInPreview={false}
                  onAdd={handleAttachmentsAdd}
                />

                <TableContainer component={Paper}>
                  <KmFormEditTable
                    columns={attachmentCols}
                    rows={attachments}
                    caption="Załączniki"
                    setRows={setAttachments}
                    editable={false}
                  />
                </TableContainer>
              </FormControl>

              <FormControlLabel
                control={
                  <Controller
                    name="emailcopy"
                    control={control}
                    render={(props) => (
                      <Checkbox
                        {...props}
                        id="autovotingend"
                        checked={copySend}
                        onChange={(e) =>
                          handleSendCopyChange(e)
                        }
                        name="emailcopy"
                        color="primary"
                      />
                    )}
                  />
                }
                label="wyslij kopię e-maila do mnie"
              />

              <Box className={classes.paddingtop}>
                <Button
                  color="primary"
                  variant="contained"
                  className={classes.button}
                  endIcon={<SendIcon />}
                  type="submit"
                >
                  Wyślij
                </Button>
              </Box>
            </form>
          </Grid>
        </Grid>
      </Box>
    </Card>
  );
};

export default Form;
